/**
 * @file  src\pages\AgentCreateOrEditWantAdDraftListing.js
 * @brief  This file is responsible for agent want ad creation start page
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import MyDrafts from "../components/Common/MyDrafts";
import { useNavigate } from "react-router-dom";
import {
  AGENT_WANTAD_KEY,
  BUY,
} from "../constants/common";
import {
  getMyDraft,
  getWantAdById,
  saveMemberWantAd,
  getProfileSlug,
} from "../services/v2/wantAdService";
import { pageURLs } from "@/constants";
import {
  getUserType,
  loggedInUser,
} from "../helpers/common";
import { useDispatch, useSelector } from "react-redux";
import CustomLinkModal from "@/components/Common/CustomLinkModal";
import PremiumPlanModal from "../components/PlanUpgrade/PremiumPlanModal";
import { fetchUserDetails } from "../actions/userprofile";
import { PageWrapper } from "@/components/PageWrapper";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import CreateWantListingStart from "@/components/CreateWantListingStart";
import { useAppConfig } from "@/appConfig";

const AgentCreateOrEditWantAdDraftListing = (props) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userType = getUserType();

  const { shouldRestrictAgent } = useAgentGitchaRestrictions();
  const { tenant } = useAppConfig() || {};

  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );

  // null while the redux user store hasn't loaded yet; true/false once it has.
  const isPremiumUser = useMemo(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      return userDetailsFromStore[0]?.period !== 1 && userType === 2;
    }
    return null;
  }, [userDetailsFromStore, userType]);

  const agent = loggedInUser();

  // Custom link management states
  const [linkToShare, setLinkToShare] = useState("");
  const [showCustomLinkPopuop, setShowCustomLinkPopup] = useState(false);

  // control handlers for custom link popup
  const handleCustomLinkPopupOpen = () => {
    setShowCustomLinkPopup(true);
  };

  const handleCustomLinkPopupClose = () => {
    setShowCustomLinkPopup(false);
  };

  //Plan modal for non-pro agent
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const [reload, setReload] = useState(0);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  useEffect(() => {
    //Fetch user details again if membership plan is upgraded
    dispatch(fetchUserDetails());
  }, [reload]);

  useEffect(() => {
    localStorage.removeItem(AGENT_WANTAD_KEY);
  }, []);

  // Draft want ads for the "My Drafts" list; only fetched once we know
  // whether the agent is premium (matches prior gating behavior).
  const draftsQuery = useQuery({
    queryKey: ["myDraftWantAds"],
    queryFn: async () => {
      const response = await getMyDraft();
      return _.get(response, "result", null);
    },
    enabled: isPremiumUser !== null,
  });
  const myDrafts = draftsQuery.data;

  /**
   * To handle navigation to edit want ad on clicking draft want ad
   * @param {*} requestPayLoad
   */
  const handleDraftItemClick = async (requestPayLoad) => {
    const wantAdId = requestPayLoad.selectedId;
    if (!wantAdId) return;

    try {
      const response = await queryClient.fetchQuery({
        queryKey: ["wantAdById", wantAdId],
        queryFn: () => getWantAdById(wantAdId),
        // The edit form is seeded from this payload, so it must never open on
        // a cached copy - override the global stale window.
        staleTime: 0,
      });
      const responseResult = _.get(response, "result", null);
      if (response.errorCode === 0 && responseResult) {
        localStorage.setItem(
          AGENT_WANTAD_KEY,
          JSON.stringify(responseResult),
        );
        navigate(`${pageURLs.editWantAdForAgents}/${responseResult.wantAdId}`);
      } else {
        console.log("Something went wrong");
      }
    } catch (err) {
      console.log("Error", err);
    }
  };
  /**
   * To load draft want ads
   * @returns
   */

  const hasDrafts = myDrafts?.length > 0;

  const loadDrafts = () => {
    if (myDrafts) {
      const myDraftList = myDrafts?.map((md) => {
        const wantAdId = _.get(md, "Want_ad_Id");
        const CompletedPercentage = _.get(md, "CompletedPercentage", "");
        const adTypeId = _.get(md, "Buy_or_Rent", "");
        const Current_Step = _.get(md, "Current_Step", "");
        const IsReadyToReview = _.get(md, "IsReadyToReview", "");
        const IsFilledByClient = _.get(md, "IsFilledByClient", "");
        let lastUpdatedDate = new Date(_.get(md, "LastUpdatedTS", ""));
        let formattedDate = [
          ("0" + (lastUpdatedDate.getMonth() + 1)).slice(-2),
          ("0" + lastUpdatedDate.getDate()).slice(-2),
          lastUpdatedDate.getFullYear(),
        ].join("/");
        let adType = "";
        if (adTypeId === 1) {
          adType = "Buy";
        } else if (adTypeId === 2) {
          adType = "Rent (Long-term)";
        } else if (adTypeId === 3) {
          adType = "Rent (Short-term)";
        }
        let clientName;
        if (_.get(md, "ClientFirstName") && _.get(md, "ClientLastName")) {
          clientName =
            _.get(md, "ClientFirstName") + " " + _.get(md, "ClientLastName");
        } else if (_.get(md, "ClientFirstName")) {
          clientName = _.get(md, "ClientFirstName");
        }

        return (
          <MyDrafts
            key={wantAdId}
            itemId={wantAdId}
            CompletedPercentage={CompletedPercentage}
            adType={adType}
            Current_Step={Current_Step}
            setDraftSelectedItem={handleDraftItemClick}
            latUpdatedDate={formattedDate}
            clientName={clientName}
            isFromWantAd={true}
            IsFilledByClient={IsFilledByClient}
            IsReadyToReview={IsReadyToReview}
          />
        );
      });

      return myDraftList;
    }
  };

  const profileSlugQuery = useQuery({
    queryKey: ["profileSlug"],
    queryFn: async () => {
      const response = await getProfileSlug();
      return _.get(response, "result", null);
    },
  });
  const userSlug = profileSlugQuery.data ?? "";

  const generateCustomLinkMutation = useMutation({
    mutationFn: (requestPayLoad) => saveMemberWantAd(requestPayLoad),
    onSuccess: (response) => {
      const responseResult = _.get(response, "result", null);
      if (response.errorCode === 0 && responseResult) {
        setLinkToShare(
          process.env.REACT_APP_BUYERLISTING_SERVICE_BASE_URL +
          "/form/" +
          userSlug,
        );
        handleCustomLinkPopupOpen();
      } else {
        console.log("Something went wrong");
      }
    },
    onError: (err) => {
      console.log("Error", err);
    },
  });

  // Function to save want ad and generate custom link for buyer form
  const generateWantAdCustomLink = () => {
    // If slug is not ready, wait 2 seconds and try again.
    if (!userSlug) {
      setTimeout(() => {
        if (!userSlug) {
          alert(
            "Profile information is still loading. Please try again later.",
          );
        } else {
          processGenerateLink();
        }
      }, 2000);
      return;
    }

    processGenerateLink();
  };

  const processGenerateLink = () => {
    const requestPayLoad = {
      isFilledByClient: true,
      isAgentAsSeller: false,
      IsSavedDraft: true,
      IsPublished: false,
      cmpldStep: 1,
      cmpldPerctg: 7,
      adType: BUY,
      isAgentCreated: true,
    };

    generateCustomLinkMutation.mutate(requestPayLoad);
  };

  const PremiumPlanModalClose = () => {
    setPremiumPlanModal(false);
  };
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  const handleBuyerListingFormSelect = () => {
    if (isPremiumUser) generateWantAdCustomLink();
    else PremiumPlanModalShow();
  };

  const handleCreateNewSelect = () => navigate(pageURLs.agentCreatewantad);

  return (
    <>
      <CustomLinkModal
        show={showCustomLinkPopuop}
        onHide={handleCustomLinkPopupClose}
        linkToShare={linkToShare}
      />

      {/* Mounted only while open: a second always-mounted copy left a stray
          static backdrop swallowing every click on the options below. */}
      {showPremiumPlanModal && (
        <PremiumPlanModal
          userDetails={userDetailsFromStore[0]}
          show={showPremiumPlanModal}
          onHide={PremiumPlanModalClose}
          updateReloadParam={updateReloadParam}
          showPlanModal={PremiumPlanModalShow}
          reason="buyerListingForm"
        />
      )}

      <PageWrapper>
        <div className="main-content agent-no-login-content">
          <CreateWantListingStart
            networkName={tenant?.mlsShortName || "MLS"}
            showBuyerForm={!shouldRestrictAgent}
            isPro={isPremiumUser === true}
            onCreate={handleCreateNewSelect}
            onSendForm={handleBuyerListingFormSelect}
            onTryPro={PremiumPlanModalShow}
            sendingForm={generateCustomLinkMutation.isPending}
            drafts={hasDrafts ? loadDrafts() : null}
          />
        </div>
      </PageWrapper>
    </>
  );
};

export default AgentCreateOrEditWantAdDraftListing;
